/**
 * T264 — Public player detail page.
 *
 * Route: /find-a-player/:id  (no auth required)
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Calendar, ChevronLeft, Lock, Mail, MapPin, Phone, Sparkles,
} from "lucide-react";

import { getPlayerContact, getPublicPlayer } from "../../api/coredata.api";
import useAuthStore from "../../store/auth.store";
import CareerStatsBlock from "../../components/players/CareerStatsBlock";


function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}


export default function PlayerDetail() {
  const { id } = useParams();
  const user = useAuthStore((s) => s.user);
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // T266 — Scout-gated contact. Three states: null=unfetched/locked,
  // {contact_email, contact_phone}=unlocked, "needs_subscription"=auth'd but
  // not Scout-subscribed, "needs_login"=not authenticated.
  const [contact, setContact] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getPublicPlayer(id)
      .then(({ data }) => { if (alive) setP(data); })
      .catch(() => { if (alive) setError("Player not found."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  // Try to fetch contact once we have the player + the user is signed in.
  useEffect(() => {
    if (!p || !user) {
      if (!user) setContact("needs_login");
      return;
    }
    let alive = true;
    getPlayerContact(id)
      .then(({ data }) => { if (alive) setContact(data); })
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status === 402) {
          setContact("needs_subscription");
        } else {
          setContact("error");
        }
      });
    return () => { alive = false; };
  }, [id, p, user]);

  if (loading) return <div className="p-6 text-text-secondary">Loading…</div>;
  if (error || !p) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <p className="text-text-secondary mb-2">{error || "Not found."}</p>
        <Link to="/find-a-player" className="text-button-primary hover:opacity-80">
          ← Back to players
        </Link>
      </div>
    );
  }

  const age = ageFromDob(p.date_of_birth);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 text-text-primary">
      <Link
        to="/find-a-player"
        className="inline-flex items-center text-sm text-text-secondary hover:text-text-primary mb-3 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" /> Back to players
      </Link>

      <div className="border border-bg-secondary rounded-lg p-4 sm:p-6 bg-bg-primary mb-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {p.photo_url
            ? <img src={p.photo_url} alt={p.full_name}
                   className="w-32 h-32 rounded-full object-cover border border-bg-secondary self-center sm:self-auto" />
            : <div className="w-32 h-32 rounded-full bg-bg-main border border-bg-secondary flex items-center justify-center text-text-secondary self-center sm:self-auto">No photo</div>}
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold mb-1 text-text-primary break-words">{p.full_name}</h1>
            {p.nick_name && (
              <p className="text-text-secondary mb-2">aka "{p.nick_name}"</p>
            )}
            <div className="text-sm text-text-secondary space-y-1">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                {[p.city, p.state].filter(Boolean).join(", ") || "—"}
              </div>
              {age && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 shrink-0" /> {age} years old
                </div>
              )}
              {p.daily_rate_inr && (
                <div className="text-text-primary">₹{p.daily_rate_inr} / day (indicative)</div>
              )}
            </div>

            <div className="flex flex-wrap gap-1 mt-3">
              {p.role && (
                <span className="text-xs px-2 py-0.5 bg-button-primary/20 text-button-primary border border-button-primary/30 rounded">
                  {p.role === "ALL_ROUNDER" ? "All-rounder" :
                   p.role === "WICKET_KEEPER" ? "WK" : p.role.toLowerCase()}
                </span>
              )}
              {p.batting_hand && (
                <span className="text-xs px-2 py-0.5 bg-bg-secondary text-text-primary rounded">
                  {p.batting_hand}-hand bat
                </span>
              )}
              {p.bowling_hand && (
                <span className="text-xs px-2 py-0.5 bg-bg-secondary text-text-primary rounded">
                  {p.bowling_hand}-arm {p.bowling_type?.toLowerCase() || "bowl"}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-1 mt-2">
              {(p.formats_played || []).map((f) => (
                <span key={f} className="text-xs px-2 py-0.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded">{f}</span>
              ))}
            </div>
          </div>
        </div>

        {p.bio && (
          <div className="mt-4 pt-4 border-t border-bg-secondary">
            <p className="text-sm text-text-secondary whitespace-pre-wrap break-words">{p.bio}</p>
          </div>
        )}
      </div>

      {/* T267 — Career stats roll-up. Renders only if any matches exist. */}
      <CareerStatsBlock stats={p.career_stats} />

      {/* T266 — Contact section, gated by Scout subscription. */}
      <div className="border border-bg-secondary rounded-lg p-4 bg-bg-primary">
        <h2 className="font-semibold mb-3 flex items-center gap-2 text-text-primary">
          {contact && typeof contact === "object"
            ? <Mail className="w-4 h-4 text-emerald-400" />
            : <Lock className="w-4 h-4 text-text-secondary" />}
          Contact
        </h2>

        {contact && typeof contact === "object" && (
          <div className="text-sm space-y-1">
            {contact.contact_email && (
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-text-secondary shrink-0" />
                <a href={`mailto:${contact.contact_email}`} className="text-button-primary hover:underline break-all">
                  {contact.contact_email}
                </a>
              </div>
            )}
            {contact.contact_phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-text-secondary shrink-0" />
                <a href={`tel:${contact.contact_phone}`} className="text-button-primary hover:underline">
                  {contact.contact_phone}
                </a>
              </div>
            )}
            {!contact.contact_email && !contact.contact_phone && (
              <p className="text-text-secondary">Player hasn't published contact details.</p>
            )}
          </div>
        )}

        {contact === "needs_subscription" && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
            <div className="flex items-start gap-2">
              <Sparkles className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-amber-200 mb-1">
                  Subscribe to Scout to see contact details
                </p>
                <p className="text-sm text-amber-100/80 mb-2">
                  Scout (₹4,999/mo) unlocks contact email + phone for every
                  player on the marketplace, plus advanced filters and
                  saved shortlists.
                </p>
                <Link to="/subscriptions#plan-B2B_SCOUT"
                      className="inline-block px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm transition-colors">
                  See Scout plan
                </Link>
              </div>
            </div>
          </div>
        )}

        {contact === "needs_login" && (
          <div className="rounded-lg border border-button-primary/30 bg-button-primary/10 p-3 text-sm text-text-primary">
            <Link to={`/login?next=/find-a-player/${id}`} className="text-button-primary hover:underline">
              Log in
            </Link>{" "}
            or{" "}
            <Link to="/signup?persona=scout" className="text-button-primary hover:underline">
              sign up as a scout
            </Link>{" "}
            to contact this player.
          </div>
        )}

        {contact === null && user && (
          <p className="text-sm text-text-secondary">Checking your access…</p>
        )}

        {contact === "error" && (
          <p className="text-sm text-rose-300">
            Couldn't load contact. Try again later.
          </p>
        )}
      </div>

      <p className="mt-6 text-xs text-text-secondary text-center">
        ZenCricketData publishes player profiles but doesn't manage hiring contracts.
        Verify credentials and discuss terms directly with the player.
      </p>
    </div>
  );
}
